import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import './IngredientReconciliationRegistry.css';
import './BillMemoryPage.css';
import * as api from '../billMemory/api';
import UnitMappingService from '../services/unitMappingService';
import LearnedNamesTab from './billMemory/LearnedNamesTab';
import VendorsTab from './billMemory/VendorsTab';
import DictionaryTab from './billMemory/DictionaryTab';
import ActivityTab from './billMemory/ActivityTab';
import AdminPanel from './billMemory/AdminPanel';
import CheckTab from './billMemory/CheckTab';
import RecentGreens from './billMemory/RecentGreens';

const TABS = [['names', 'Learned names'], ['vendors', 'Vendors'], ['dictionary', 'Dictionary'], ['activity', 'Activity']];
// Fix plan I3 / I2: Owner and Managers check what Bill Memory filled
const EDIT_TABS = [['check', 'Check'], ['recent', 'Recent greens']];

/**
 * Settings › Bill Memory (source spec, "Settings: the Bill Memory page"; design §15). One plain
 * table of everything the app has learned for this business, how it learned it, and the controls
 * to change it — in the same table, tab and button styles as Ingredient Reconciliation.
 * Owner / manage_inventory edit, stock_in reads (the server enforces it on every call).
 */
export default function BillMemoryPage({ onBack, businessId: propBusinessId }) {
  const businessId = propBusinessId || UnitMappingService.getBusinessId();
  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState('names');
  const [gstin, setGstin] = useState('');
  const [gstinMsg, setGstinMsg] = useState('');

  useEffect(() => {
    api.getAccess().then((a) => { setAccess(a); setGstin(a.ownGstin || ''); }).catch(() => setAccess({ canView: false }));
  }, []);

  const header = (
    <header className="irr-header">
      <div className="irr-shell irr-header__inner">
        <div className="irr-title-row">
          <div className="irr-header-left">
            <button type="button" onClick={onBack || undefined} className="irr-back-btn" aria-label="Back">
              <ArrowLeft size={20} />
            </button>
            <h1>Bill Memory</h1>
          </div>
        </div>
      </div>
    </header>
  );

  if (!access) return <div className="irr-page">{header}<main className="irr-shell irr-main"><div className="irr-empty">Loading…</div></main></div>;
  if (!access.canView) {
    return <div className="irr-page">{header}<main className="irr-shell irr-main"><div className="irr-empty">You do not have access to Bill Memory.</div></main></div>;
  }

  return (
    <div className="irr-page bm-page">
      {header}
      <main className="irr-shell irr-main">
        {access.mode === 'shadow' && (
          <div className="bm-note">
            Bill Memory is learning from your stock-ins in the background. Stock In works as before until it turns on for your business by itself.
          </div>
        )}
        <div className="bm-gstin">
          <label className="bm-field">
            Your GSTIN (printed on every bill as the buyer — never read as a vendor)
            <input className="bm-input" value={gstin} disabled={!access.canEdit} onChange={(e) => setGstin(e.target.value.toUpperCase())} placeholder="e.g. 36ABJFM4561E1Z8" />
          </label>
          {access.canEdit && (
            <button type="button" className="irr-secondary-btn" onClick={async () => {
              setGstinMsg('');
              try { const r = await api.setOwnGstin(gstin); setGstin(r.own_gstin || ''); setGstinMsg('Saved.'); } catch (e) { setGstinMsg('That GSTIN does not pass its check digit.'); }
            }}>Save</button>
          )}
          {gstinMsg && <span className="bm-note">{gstinMsg}</span>}
        </div>

        <div className="irr-tabs" role="tablist" aria-label="Bill Memory">
          {[...TABS, ...(access.canEdit ? EDIT_TABS : [])].map(([key, label]) => (
            <button type="button" role="tab" aria-selected={tab === key} key={key} onClick={() => setTab(key)}
              className={`irr-tab${tab === key ? ' irr-tab--active' : ''}`}>{label}</button>
          ))}
        </div>

        {tab === 'names' && <LearnedNamesTab canEdit={access.canEdit} businessId={businessId} />}
        {tab === 'vendors' && <VendorsTab />}
        {tab === 'dictionary' && <DictionaryTab canEdit={access.canEdit} />}
        {tab === 'activity' && <ActivityTab />}
        {tab === 'check' && access.canEdit && <CheckTab />}
        {tab === 'recent' && access.canEdit && <RecentGreens />}

        {access.isAdmin && <AdminPanel businessId={businessId} />}
      </main>
    </div>
  );
}
